// Platform-admin hooks over the billing usage counts (spec 2026-10-05-12):
// GET /api/v1/admin/billing/usage and
// /api/v1/admin/billing/organizations/{orgUid}/usage. Read-only, cross-org,
// never visible to organizations.
import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { $api } from '../api/queryClient'
import type { components } from '../api/schema'

export type AdminBillingMetric = components['schemas']['AdminBillingMetric']
export type AdminBillingUsageList = components['schemas']['AdminBillingUsageList']
export type AdminBillingOrganizationUsage = components['schemas']['AdminBillingOrganizationUsage']

/** useAdminBillingUsage loads every organization with its latest daily values. */
export function useAdminBillingUsage() {
    return $api.useQuery('get', '/api/v1/admin/billing/usage')
}

/** useAdminBillingHistory loads the daily rows of one organization over [from, to]. */
export function useAdminBillingHistory(orgUid: string, from: string, to: string) {
    return $api.useQuery('get', '/api/v1/admin/billing/organizations/{orgUid}/usage', {
        params: { path: { orgUid }, query: { from, to } },
    })
}

/** formatBillingValue prints a metric value in a readable unit. */
export function formatBillingValue(value: number | undefined, unit: string, locale: string): string {
    if (value === undefined) return '—'
    if (unit !== 'bytes') return new Intl.NumberFormat(locale).format(value)
    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    let n = value
    let i = 0
    while (n >= 1024 && i < units.length - 1) {
        n /= 1024
        i++
    }
    const digits = i === 0 ? 0 : n < 10 ? 2 : 1
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(n)} ${units[i]}`
}

export type AdminBillingAccount = components['schemas']['AdminBillingAccount']
export type AdminBillingPrice = components['schemas']['AdminBillingPrice']
export type AdminBillingPriceCode = components['schemas']['AdminBillingPriceCode']
export type AdminBillingConsumptionMonth = components['schemas']['AdminBillingConsumptionMonth']

// Accounts, price list and monthly closing (spec 2026-10-05-13). Any write
// refreshes every billing query: a price changes what the page shows twice.
function useInvalidateBilling() {
    const qc = useQueryClient()
    return () =>
        qc.invalidateQueries({
            predicate: (query) => {
                const key = query.queryKey as unknown[]
                return key[0] === 'get' && typeof key[1] === 'string' && key[1].startsWith('/api/v1/admin/billing')
            },
        })
}

export function useAdminBillingAccount(orgUid: string) {
    return $api.useQuery('get', '/api/v1/admin/billing/organizations/{orgUid}/account', { params: { path: { orgUid } } })
}

export function usePutAdminBillingAccount() {
    const invalidate = useInvalidateBilling()
    return $api.useMutation('put', '/api/v1/admin/billing/organizations/{orgUid}/account', { onSuccess: invalidate })
}

/** useAdminBillingPrices loads the rows of one organization, or the list price rows without orgUid. */
export function useAdminBillingPrices(orgUid?: string) {
    return $api.useQuery('get', '/api/v1/admin/billing/prices', {
        params: { query: orgUid ? { organization_uid: orgUid } : {} },
    })
}

export function useCreateAdminBillingPrice() {
    const invalidate = useInvalidateBilling()
    return $api.useMutation('post', '/api/v1/admin/billing/prices', { onSuccess: invalidate })
}

export function useDeleteAdminBillingPrice() {
    const invalidate = useInvalidateBilling()
    return $api.useMutation('delete', '/api/v1/admin/billing/prices/{uid}', { onSuccess: invalidate })
}

export function useAdminBillingConsumptions(orgUid: string) {
    return $api.useQuery('get', '/api/v1/admin/billing/organizations/{orgUid}/consumptions', {
        params: { path: { orgUid } },
    })
}

export function useRecloseAdminBillingMonth() {
    const invalidate = useInvalidateBilling()
    return $api.useMutation('post', '/api/v1/admin/billing/organizations/{orgUid}/months/{month}/reclose', {
        onSuccess: invalidate,
    })
}

/** eurosToMicros converts "0.125" euros to 125000 micro-euros without a float. */
export function eurosToMicros(input: string): number | null {
    const m = /^(\d+)(?:[.,](\d{1,6}))?$/.exec(input.trim())
    if (!m) return null
    return Number(m[1]) * 1_000_000 + Number((m[2] ?? '').padEnd(6, '0'))
}

/** microsToEuros prints micro-euros as euros, trimming the useless decimals. */
export function microsToEuros(micros: number, locale: string): string {
    return new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 6 }).format(micros / 1_000_000)
}

/** formatCents prints cents as euros. */
export function formatCents(cents: number, locale: string): string {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(cents / 100)
}

export type AdminBillingInvoice = components['schemas']['AdminBillingInvoice']
export type AdminBillingInvoiceDetail = components['schemas']['AdminBillingInvoiceDetail']
export type AdminBillingInvoiceLine = components['schemas']['AdminBillingInvoiceLine']

// Invoices and payments (spec 2026-10-05-14).
export interface AdminBillingInvoiceFilter {
    organization_uid?: string
    kind?: 'monthly' | 'annual' | 'annual_overage'
    payment_state?: 'draft' | 'due' | 'overdue' | 'paid' | 'cancelled'
    from?: string
    to?: string
}

export function useAdminBillingInvoices(filter: AdminBillingInvoiceFilter) {
    return $api.useQuery('get', '/api/v1/admin/billing/invoices', { params: { query: filter } })
}

export function useAdminBillingInvoice(uid: string) {
    return $api.useQuery('get', '/api/v1/admin/billing/invoices/{uid}', { params: { path: { uid } } })
}

export function useIssueAdminBillingInvoice() {
    const invalidate = useInvalidateBilling()
    return $api.useMutation('post', '/api/v1/admin/billing/invoices/{uid}/issue', { onSuccess: invalidate })
}

export function useCancelAdminBillingInvoice() {
    const invalidate = useInvalidateBilling()
    return $api.useMutation('post', '/api/v1/admin/billing/invoices/{uid}/cancel', { onSuccess: invalidate })
}

export function useAddAdminBillingReduction() {
    const invalidate = useInvalidateBilling()
    return $api.useMutation('post', '/api/v1/admin/billing/invoices/{uid}/reductions', { onSuccess: invalidate })
}

export function useDeleteAdminBillingInvoiceLine() {
    const invalidate = useInvalidateBilling()
    return $api.useMutation('delete', '/api/v1/admin/billing/invoice-lines/{uid}', { onSuccess: invalidate })
}

export function useAddAdminBillingPayment() {
    const invalidate = useInvalidateBilling()
    return $api.useMutation('post', '/api/v1/admin/billing/invoices/{uid}/payments', { onSuccess: invalidate })
}

export function useDeleteAdminBillingPayment() {
    const invalidate = useInvalidateBilling()
    return $api.useMutation('delete', '/api/v1/admin/billing/payments/{uid}', { onSuccess: invalidate })
}

/** eurosToCents converts "12.34" euros to 1234 cents, null when the input isn't an amount. */
export function eurosToCents(input: string): number | null {
    const m = /^(\d+)(?:[.,](\d{1,2}))?$/.exec(input.trim())
    if (!m) return null
    return Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0'))
}

/** centsToInput prints cents as an editable amount: 1234 becomes "12.34". */
export function centsToInput(cents: number): string {
    return (cents / 100).toFixed(2)
}

export type AdminBillingIncluded = components['schemas']['AdminBillingIncluded']

// Prepaid quantities and the usage recompute (spec 2026-10-08-01).
export function useAdminBillingIncluded(orgUid: string) {
    return $api.useQuery('get', '/api/v1/admin/billing/organizations/{orgUid}/included', { params: { path: { orgUid } } })
}

export function useCreateAdminBillingIncluded() {
    const invalidate = useInvalidateBilling()
    return $api.useMutation('post', '/api/v1/admin/billing/organizations/{orgUid}/included', { onSuccess: invalidate })
}

export function useDeleteAdminBillingIncluded() {
    const invalidate = useInvalidateBilling()
    return $api.useMutation('delete', '/api/v1/admin/billing/organizations/{orgUid}/included/{uid}', { onSuccess: invalidate })
}

export type AdminBillingRecomputeResult = components['schemas']['AdminBillingRecomputeResult']

export function useRecomputeAdminBillingUsage() {
    return $api.useMutation('post', '/api/v1/admin/billing/organizations/{orgUid}/usage/recompute')
}

/** isRecomputeDone is true once the job has a final status. */
export function isRecomputeDone(status: AdminBillingRecomputeResult['status'] | undefined): boolean {
    return status === 'success' || status === 'failed' || status === 'cancelled'
}

/**
 * useAdminBillingRecomputeJob polls a usage recompute every second until it
 * ends, then refreshes every billing query so the usage table shows the new
 * counts. The admin pages have no live socket (no :org in the URL), so
 * polling is how the page learns the job is done.
 */
export function useAdminBillingRecomputeJob(orgUid: string, jobUid: string | null) {
    const invalidate = useInvalidateBilling()
    const query = $api.useQuery(
        'get',
        '/api/v1/admin/billing/organizations/{orgUid}/usage/recompute/{jobUid}',
        { params: { path: { orgUid, jobUid: jobUid ?? '' } } },
        {
            enabled: jobUid !== null,
            refetchInterval: (q) => (isRecomputeDone(q.state.data?.status) ? false : 1000),
        },
    )
    const done = isRecomputeDone(query.data?.status)
    const refreshed = useRef<string | null>(null)
    useEffect(() => {
        if (done && jobUid && refreshed.current !== jobUid) {
            refreshed.current = jobUid
            void invalidate()
        }
    }, [done, jobUid, invalidate])
    return query
}

/** useSimulateAdminBilling runs the simulator (spec 2026-10-09-02): nothing is written server side. */
export function useSimulateAdminBilling() {
    return $api.useMutation('post', '/api/v1/admin/billing/simulate')
}
